/**
 * Marketing (promo) cards for the explorer grid (#1145) — pure helpers.
 *
 * Port of ckit's Marketing Cards tool (`assets/js/tools/marketing-cards.js`,
 * `includes/Tools/MarketingCards/Renderer.php`), folded into the explorer
 * widget. The WP engine is a MutationObserver bolt-on that diffs the live DOM
 * against a desired layout; the platform grid is React-rendered, so placement
 * is a pure function of `(cards, frequency, propertyCount)` computed during
 * render (`computeMarketingCardPositions`). Same determinism, none of the
 * DOM-diffing — React's render model already guarantees the same inputs give
 * the same interleave across re-renders and infinite-scroll page loads.
 *
 * No React imports here on purpose: everything is unit-testable in isolation.
 */
import {
  isCssSafeCardUrl,
  isSafeCardHref,
  isSafeCardLinkHref,
  type MarketingCard,
  type MarketingCardsGlobalStyle,
} from './config'
import { inlineHtmlVisibleText } from '../../common/sanitize/inline-html'
import { hexToRgb } from './hooks/searchbarCss'

export { isSafeCardHref, isSafeCardLinkHref }

/** One card-insertion slot. `pos` = the 1-based count of real listings the card follows. */
export interface MarketingCardPlacement {
  /** Insert after the `pos`-th real listing (pos = frequency, 2×frequency, …). */
  pos: number
  /** Index into the ACTIVE card list (`activeMarketingCards`; round-robin in authored order). */
  cardIndex: number
  /** 1-based slot number — the impression de-dupe key alongside the card id. */
  ordinal: number
}

/**
 * WP `computeDesired(realCount)`: positions are exactly FREQ, 2·FREQ, 3·FREQ …
 * up to the current real-listing count, round-robining through the enabled
 * cards in authored order. Below `frequency` listings nothing is inserted
 * (WP's `skipped_below_n` branch). Deterministic for a given input triple.
 */
export function computeMarketingCardPositions(
  cardCount: number,
  frequency: number,
  propertyCount: number,
): MarketingCardPlacement[] {
  // WP: `Math.max(2, parseInt(cfg.frequency, 10) || 6)` — unparsable/zero
  // falls back to the default 6, then the MIN_FREQUENCY floor applies.
  const parsed = Math.floor(Number(frequency))
  const freq = Math.max(2, Number.isFinite(parsed) && parsed > 0 ? parsed : 6)
  if (cardCount <= 0 || propertyCount < freq) return []
  const out: MarketingCardPlacement[] = []
  let k = 1
  for (let pos = freq; pos <= propertyCount; pos += freq) {
    out.push({ pos, cardIndex: (k - 1) % cardCount, ordinal: k })
    k++
  }
  return out
}

/** A card that takes slots in the grid, with its event id resolved. */
export type ActiveMarketingCard = MarketingCard & { id: string }

/**
 * WP `Renderer::build()` skips disabled cards and cards with no content (no
 * title, body, inline image, or cover image) — an empty panel in the grid is
 * never useful. Order is preserved so round-robin follows authored order.
 *
 * Ids are resolved HERE, from the authored position, before filtering: WP
 * mints persistent ids at save time, so disabling or blanking one card must
 * never re-label the cards after it (`card-2` stays `card-2` when `card-1`
 * is switched off) — otherwise analytics merge different cards under one id.
 */
export function activeMarketingCards(cards: MarketingCard[]): ActiveMarketingCard[] {
  return cards
    .map((c, authoredIndex) => ({ ...c, id: marketingCardId(c, authoredIndex) }))
    .filter(
      (c) =>
        c.enabled &&
        Boolean(
          c.title ||
          // A rich body counts as content only by its VISIBLE text (#1163
          // round 2): a body the sanitizer reduces to nothing perceivable
          // (`<script>sale</script>`, a bare `<br>`) must not buy a slot the
          // card then renders blank. Plain bodies are the literal text.
          (c.bodyRichText === true ? inlineHtmlVisibleText(c.body) !== '' : Boolean(c.body)) ||
          c.imageUrl ||
          // A background image counts as content only if it can actually be
          // emitted as CSS — otherwise the card takes a slot and renders blank.
          safeCssUrl(c.bgImageUrl) !== '',
        ),
    )
}

/** Stable per-card id: the authored id, else its 1-based position in the AUTHORED list. */
export function marketingCardId(card: MarketingCard, authoredIndex: number): string {
  return card.id && card.id !== '' ? card.id : `card-${authoredIndex + 1}`
}

/**
 * Port of `Renderer::hex_to_rgba()`: #rgb / #rrggbb → `rgba(r, g, b, a)` with
 * the alpha clamped to 0..1 and printed to two decimals, trailing zeros
 * trimmed (`0.4`, not `0.40`; `1`, not `1.00`). Malformed hex falls back to
 * black — the scrim must always be a real color so copy stays legible.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(String(hex ?? '')) ?? { r: 0, g: 0, b: 0 }
  const a = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 0))
  const aStr = a.toFixed(2).replace(/\.?0+$/, '') || '0'
  return `rgba(${r}, ${g}, ${b}, ${aStr})`
}

export interface ResolvedCardColors {
  bgColor: string
  textColor: string
  buttonBg: string
  buttonText: string
}

/**
 * Port of `Renderer::resolve_colors()`: a per-card override wins only when
 * `useGlobalStyle` is off AND the override is set; otherwise the global style
 * value applies. Overrides are schema-validated hex (malformed → absent).
 */
export function resolveCardColors(
  card: MarketingCard,
  globalStyle: MarketingCardsGlobalStyle,
): ResolvedCardColors {
  const pick = (override: string | undefined, global: string) =>
    !card.useGlobalStyle && override ? override : global
  return {
    bgColor: pick(card.bgColor, globalStyle.bgColor),
    textColor: pick(card.textColor, globalStyle.textColor),
    buttonBg: pick(card.buttonBg, globalStyle.buttonBg),
    buttonText: pick(card.buttonText, globalStyle.buttonText),
  }
}

/**
 * A URL that is safe to interpolate inside `url("…")` in an inline style.
 * The schema already restricts the shape (https:// or /relative); this is the
 * render-time defense-in-depth against a value that could terminate the
 * `url(` token or the declaration: quotes, parens, backslash, whitespace,
 * `;{}<>` are all rejected → no background image rather than a breakout.
 * The predicate (`isCssSafeCardUrl`, config.ts) is the allowlist itself and
 * is shared with the dashboard's image-only advisory, so both sides agree on
 * which background images actually render.
 */
export function safeCssUrl(url: string | undefined): string {
  if (!url) return ''
  const v = url.trim()
  return isCssSafeCardUrl(v) ? v : ''
}

/** Render-time ASSET href guard (https/relative) mirroring the schema refine (never trust a stored value at emission). */
export function safeCardHref(url: string | undefined): string {
  if (!url) return ''
  const v = url.trim()
  return v !== '' && isSafeCardHref(v) ? v : ''
}

/** Render-time LINK href guard: the asset policy plus mailto:/tel: (#1163). */
export function safeCardLinkHref(url: string | undefined): string {
  if (!url) return ''
  const v = url.trim()
  return v !== '' && isSafeCardLinkHref(v) ? v : ''
}

/**
 * A link that hands off to a protocol handler (mail client, dialer) rather
 * than loading a page — there is no tab to open, so "open in new tab" never
 * applies. Only ever called on a value that already passed the link policy.
 */
export function isProtocolHandlerHref(href: string): boolean {
  return /^(?:mailto|tel):/i.test(href)
}

// ── Events (impressions + CTA clicks) ───────────────────────────────────────
//
// WP calls `window.hrckInsights.track('marketing_cards', action, meta)`, a
// WP-side analytics pipeline the platform does not have (#1145 brief: zero
// hits for any tracking bridge in this package). v1 mirrors the repo's
// existing client-side pattern (related-properties #717 phase 1): a
// bubbling+composed CustomEvent on the card element (host pages hear it
// across the shadow boundary) plus a `window.dataLayer` push when one exists.
// NO network call. B3c EXTENSION POINT: when the `events` ingest endpoint
// exists, add the fetch inside `emit()` below — every producer already
// funnels through it.

export type MarketingCardAction = 'impression' | 'cta_click'

export interface MarketingCardEventContext {
  widgetType: string
  widgetId: string
  feedId: number
}

export interface MarketingCardEventDetail extends MarketingCardEventContext {
  action: MarketingCardAction
  cardId: string
  ordinal: number
  url?: string
}

export const MARKETING_CARD_EVENT = 'hr-marketing-card'

/**
 * Pure event recorder. Impressions are de-duped for the tracker's lifetime
 * (one tracker = one widget mount = one page view) on the `cardId|ordinal`
 * key, exactly WP's per-container `seen` set: a card re-rendered at the same
 * slot after a re-render/page load counts once; the same card at a NEW slot
 * (round-robin after more listings load) counts again. CTA clicks are never
 * de-duped. Returns the emitted details so tests can observe without listening.
 */
export function createMarketingCardTracker(ctx: MarketingCardEventContext) {
  const seen = new Set<string>()

  const emit = (
    detail: MarketingCardEventDetail,
    target: Element | null,
    dataLayer?: unknown,
  ) => {
    target?.dispatchEvent(
      new CustomEvent(MARKETING_CARD_EVENT, { detail, bubbles: true, composed: true }),
    )
    if (Array.isArray(dataLayer)) {
      dataLayer.push({
        event: 'hr_marketing_card',
        hr_action: detail.action,
        hr_widget_type: detail.widgetType,
        hr_widget_id: detail.widgetId,
        hr_feed_id: detail.feedId,
        hr_card_id: detail.cardId,
        hr_ordinal: detail.ordinal,
        ...(detail.url !== undefined ? { hr_url: detail.url } : {}),
      })
    }
  }

  return {
    impression(
      cardId: string,
      ordinal: number,
      target: Element | null,
      dataLayer?: unknown,
    ): MarketingCardEventDetail | null {
      const key = `${cardId}|${ordinal}`
      if (seen.has(key)) return null
      seen.add(key)
      const detail: MarketingCardEventDetail = { ...ctx, action: 'impression', cardId, ordinal }
      emit(detail, target, dataLayer)
      return detail
    },
    ctaClick(
      cardId: string,
      ordinal: number,
      url: string,
      target: Element | null,
      dataLayer?: unknown,
    ): MarketingCardEventDetail {
      const detail: MarketingCardEventDetail = { ...ctx, action: 'cta_click', cardId, ordinal, url }
      emit(detail, target, dataLayer)
      return detail
    },
  }
}

export type MarketingCardTracker = ReturnType<typeof createMarketingCardTracker>
